import {
  useState, useEffect, useMemo, useRef,
} from 'react';
import {
  PENDING, PRISTINE, FAILURE, SUCCESS,
} from '../constants/queryStatuses';
import { useIsMounted } from './useIsMounted';

const getDataKeyDefault = (fetchParams) => JSON.stringify(fetchParams);

export const useFetchData = ({
  fetchData, fetchParams, dataKey, initState = null, getDataKey = getDataKeyDefault, isActive,
}) => {
  const [state, setState] = useState({ status: PRISTINE, data: initState });
  const isMounted = useIsMounted();
  const abortControllerRef = useRef(null);
  const requestCounterRef = useRef(0);

  const finalDataKey = useMemo(() => {
    if (!isActive) {
      return null;
    }
    return dataKey ?? getDataKey(fetchParams);
  }, [isActive, dataKey, getDataKey, fetchParams]);

  useEffect(() => {
    if (!isActive) {
      return () => {};
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const { signal } = abortController;

    requestCounterRef.current += 1;
    const currentRequestNumber = requestCounterRef.current;

    setState({ data: null, status: PENDING });

    fetchData(fetchParams, { signal })
      .then((result) => {
        if (!isMounted() || currentRequestNumber !== requestCounterRef.current) {
          return;
        }
        setState({ data: result, status: SUCCESS });
      })
      .catch((ex) => {
        if (!isMounted() || currentRequestNumber !== requestCounterRef.current) {
          return;
        }
        console.error(`useFetchData ${finalDataKey} ex:`, ex);
        setState({ data: null, status: FAILURE });
      });

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, fetchParams, finalDataKey, isActive, isMounted]);

  return state;
};
